import { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';

/**
 * Setas de rolagem horizontal do board (detecta overflow e scroll suave).
 */
export function useBoardScroll(
  isAuthenticated: boolean,
  columns: { id: string }[],
  zoomLevel: number,
) {
  const boardScrollRef = useRef<HTMLDivElement>(null);
  const boardScrollContentRef = useRef<HTMLDivElement>(null);
  const [boardCanScrollLeft, setBoardCanScrollLeft] = useState(false);
  const [boardCanScrollRight, setBoardCanScrollRight] = useState(false);

  const updateBoardScrollArrows = useCallback(() => {
    const el = boardScrollRef.current;
    const inner = boardScrollContentRef.current;
    if (!el) return;
    const scrollWidth = Math.max(el.scrollWidth, inner?.scrollWidth ?? 0);
    const { scrollLeft, clientWidth } = el;
    const eps = 2;
    const canScroll = scrollWidth > clientWidth + eps;
    setBoardCanScrollLeft(canScroll && scrollLeft > eps);
    setBoardCanScrollRight(canScroll && scrollLeft + clientWidth < scrollWidth - eps);
  }, []);

  const scrollBoardHorizontal = useCallback((direction: 'left' | 'right') => {
    const el = boardScrollRef.current;
    if (!el) return;
    const amount = Math.max(240, Math.floor(el.clientWidth * 0.65));
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const el = boardScrollRef.current;
    const inner = boardScrollContentRef.current;
    if (!el) return;
    updateBoardScrollArrows();
    el.addEventListener('scroll', updateBoardScrollArrows, { passive: true });
    const ro = new ResizeObserver(() => updateBoardScrollArrows());
    ro.observe(el);
    if (inner) ro.observe(inner);
    return () => {
      el.removeEventListener('scroll', updateBoardScrollArrows);
      ro.disconnect();
    };
  }, [isAuthenticated, updateBoardScrollArrows, columns]);

  useLayoutEffect(() => {
    if (!isAuthenticated) return;
    updateBoardScrollArrows();
    const t = window.setTimeout(() => updateBoardScrollArrows(), 50);
    return () => clearTimeout(t);
  }, [isAuthenticated, updateBoardScrollArrows, columns, zoomLevel]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const id = requestAnimationFrame(() => updateBoardScrollArrows());
    return () => cancelAnimationFrame(id);
  }, [isAuthenticated, updateBoardScrollArrows, zoomLevel]);

  return {
    boardScrollRef,
    boardScrollContentRef,
    boardCanScrollLeft,
    boardCanScrollRight,
    scrollBoardHorizontal,
  };
}
